import { listStringify, NoAndNoComma } from './listStringify'

interface LayoutOption {
  /**
   * include zero values
   * @default false
   */
  zero?: boolean
  /**
   * don't include this value
   * @default false
   */
  exclude?: boolean
  /**
   * use Number.toFixed instead of Math.floor with the number of decimals
   */
  decimals?: number
}

interface Layout {
  /**
   * include milliseconds in the result
   * @default false
   */
  includeMs?: boolean
  /**
   * layout options for days
   */
  days?: LayoutOption
  /**
   * layout options for hours
   */
  hours?: LayoutOption
  /**
   * layout options for minutes
   */
  minutes?: LayoutOption
  /**
   * layout options for seconds
   */
  seconds?: LayoutOption
}

interface Format extends NoAndNoComma {
  /**
   * options to force a specific layout in the formatted return value
   *
   * by default, excludes milliseconds and only includes non-zero values
   */
  layout?: Layout
}

interface WithFormat {
  /**
   * the format of the return values can be customized using this property
   */
  format?: Format
}

interface ElapsedWithDates extends WithFormat {
  /**
   * the start date
   */
  start: Date
  /**
   * the end date
   */
  end: Date
  milliseconds?: never
}

interface ElapsedWithTime extends WithFormat {
  /**
   * the number of milliseconds
   */
  milliseconds: number
  start?: never
  end?: never
}

type ElapsedOptions = ElapsedWithDates | ElapsedWithTime

interface Value {
  /**
   * the total elapsed time for the given unit
   */
  total: string
  /**
   * the human readable elapsed time for the given unit
   */
  reduced: string
  /**
   * the total elapsed time in number format
   */
  raw: number
}

interface Values {
  /**
   * days
   */
  d: Value
  /**
   * hours
   */
  h: Value
  /**
   * minutes
   */
  m: Value
  /**
   * seconds
   */
  s: Value
  /**
   * milliseconds
   */
  ms: Value
}

interface Elapsed {
  /**
   * the formatted string
   */
  formatted: string
  /**
   * values that make up `formatted`
   *
   * `reduced` and `total` will follow any `layout.decimals` properties for each
   * associated value
   */
  values: Values
}

/**
 * Given a starting date and ending date or number of milliseconds, generates a
 * string of the total duration, along with an object containing the values for
 * the elapsed string
 */
export const elapsed = (options: ElapsedOptions): Elapsed => {
  const { start, end, format, milliseconds: millisecondsOption } = options
  const { layout, noAndOr: noAnd, noComma } = format ?? {}
  const { days, hours, includeMs = false, minutes, seconds } = layout ?? {}

  const elapsed = millisecondsOption
    ? millisecondsOption
    : Number(end) - Number(start)

  const MILLISECONDS_SECONDS = 1000
  const MILLISECONDS_MINUTES = 60 * MILLISECONDS_SECONDS
  const MILLISECONDS_HOURS = 60 * MILLISECONDS_MINUTES
  const MILLISECONDS_DAYS = 24 * MILLISECONDS_HOURS

  const d: _Transformer = (num, units) => day(num, days?.decimals, units)
  const h: _Transformer = (num, units) => hour(num, hours?.decimals, units)
  const m: _Transformer = (num, units) => minute(num, minutes?.decimals, units)
  const s: _Transformer = (num, units) => second(num, seconds?.decimals, units)
  const ms: _Transformer = (num, units) => millisecond(num, undefined, units)

  const rawDays = elapsed / MILLISECONDS_DAYS
  const rawHours = elapsed / MILLISECONDS_HOURS
  const rawMinutes = elapsed / MILLISECONDS_MINUTES
  const rawSeconds = elapsed / MILLISECONDS_SECONDS

  // formatted section
  let milliseconds = elapsed
  const onlyMilliseconds = elapsed < 1000
  const fDays = Math.floor(milliseconds / MILLISECONDS_DAYS)
  milliseconds -= fDays * MILLISECONDS_DAYS
  const fHours = Math.floor(milliseconds / MILLISECONDS_HOURS)
  milliseconds -= fHours * MILLISECONDS_HOURS
  const fMinutes = Math.floor(milliseconds / MILLISECONDS_MINUTES)
  milliseconds -= fMinutes * MILLISECONDS_MINUTES
  const fSeconds = Math.floor(milliseconds / MILLISECONDS_SECONDS)
  milliseconds -= fSeconds * MILLISECONDS_SECONDS

  const items: string[] = []
  if (include(fDays, days)) items.push(d(fDays, true))
  if (include(fHours, hours)) items.push(h(fHours, true))
  if (include(fMinutes, minutes)) items.push(m(fMinutes, true))
  if (include(fSeconds, seconds)) items.push(s(fSeconds, true))
  if (onlyMilliseconds || includeMs) items.push(ms(milliseconds, true))

  return {
    formatted: listStringify({ items, noAndOr: noAnd, noComma }),
    values: {
      d: { reduced: d(fDays), total: d(rawDays), raw: rawDays },
      h: { reduced: h(fHours), total: h(rawHours), raw: rawHours },
      m: { reduced: m(fMinutes), total: m(rawMinutes), raw: rawMinutes },
      s: { reduced: s(fSeconds), total: s(rawSeconds), raw: rawSeconds },
      ms: { reduced: ms(milliseconds), total: ms(elapsed), raw: elapsed },
    },
  }
}

const include = (value: number, layoutOption?: LayoutOption): boolean => {
  const greater = value > 0
  if (!layoutOption) return greater
  const { exclude, zero } = layoutOption
  if (exclude) return false
  if (value <= 0 && zero) return true
  return greater
}

type _Transformer = (num: number, showUnit?: boolean) => string

type Transformer = (num: number, fixed?: number, showUnit?: boolean) => string
const isNum = (num?: number): num is number => !isNaN(Number(num))
const stringify: Transformer = (num, fixed) =>
  !isNum(fixed)
    ? Math.floor(num).toString()
    : num.toFixed(Math.min(Math.max(0, fixed), 100))
const units = (
  showUnits: boolean | undefined,
  stringified: string,
  units: string
) => {
  if (!showUnits) return stringified
  return stringified + units
}
const day: Transformer = (days, fixed, showUnits) =>
  units(showUnits, stringify(days, fixed), ` day${days === 1 ? '' : 's'}`)
const hour: Transformer = (hours, fixed, showUnits) =>
  units(showUnits, stringify(hours, fixed), 'h')
const minute: Transformer = (minutes, fixed, showUnits) =>
  units(showUnits, stringify(minutes, fixed), 'm')
const second: Transformer = (seconds, fixed, showUnits) =>
  units(showUnits, stringify(seconds, fixed), 's')
const millisecond: Transformer = (milliseconds, _, showUnits) =>
  units(showUnits, stringify(milliseconds), 'ms')
